import { BLOG_ROUTES, type BlogFeed, type BlogImage, type BlogLocale, type BlogPost } from '@/config/blog';

/**
 * The home page's blog feed, from this site's own Worker (never from Hiquill:
 * the key stays on the server and the Worker caches for everyone). One request
 * per language per page load, shared by every caller; a failure resolves to
 * null and is not retried, so the section quietly stays hidden. Where there is
 * no Worker (`vite dev`), the answer isn't JSON and the result is null too.
 */

const requests = new Map<BlogLocale, Promise<BlogFeed | null>>();

export function loadBlogFeed(locale: BlogLocale, fetcher: typeof fetch = fetch): Promise<BlogFeed | null> {
  let request = requests.get(locale);
  if (!request) {
    request = fetchFeed(locale, fetcher);
    requests.set(locale, request);
  }
  return request;
}

/** Tests only. */
export const forgetBlogFeeds = () => requests.clear();

async function fetchFeed(locale: BlogLocale, fetcher: typeof fetch): Promise<BlogFeed | null> {
  try {
    const response = await fetcher(`${BLOG_ROUTES.feed}?locale=${locale}`, { headers: { Accept: 'application/json' } });
    if (!response.ok || !response.headers.get('Content-Type')?.includes('application/json')) return null;
    return parseBlogFeed(await response.json());
  } catch {
    return null;
  }
}

type Json = Record<string, unknown>;
const isObject = (value: unknown): value is Json => typeof value === 'object' && value !== null;
const isString = (value: unknown): value is string => typeof value === 'string';
const isHttp = (value: unknown): value is string => isString(value) && /^https?:\/\//i.test(value);
/** Pictures must come from this site's Worker; anything else is dropped. */
const isOwnMedia = (value: unknown): value is string => isString(value) && value.startsWith(BLOG_ROUTES.media);
const size = (value: unknown) => (typeof value === 'number' && value > 0 ? value : null);

function image(value: unknown): BlogImage | null {
  if (!isObject(value) || !isOwnMedia(value.src)) return null;
  return { src: value.src, alt: isString(value.alt) ? value.alt : '', width: size(value.width), height: size(value.height) };
}

function post(value: unknown): BlogPost | null {
  if (!isObject(value) || !isString(value.title) || !value.title || !isHttp(value.url) || !isString(value.slug) || !isString(value.publishedAt)) return null;
  const author = isObject(value.author) && isString(value.author.name) && value.author.name ? value.author : null;
  return {
    slug: value.slug,
    title: value.title,
    excerpt: isString(value.excerpt) ? value.excerpt : '',
    locale: isString(value.locale) ? value.locale : '',
    url: value.url,
    publishedAt: value.publishedAt,
    cover: image(value.cover),
    author: author ? { name: author.name as string, avatar: isOwnMedia(author.avatar) ? author.avatar : null } : null,
    topic: isString(value.topic) && value.topic ? value.topic : null,
  };
}

/** Checks the Worker's answer; anything unexpected becomes null, never a broken section. */
export function parseBlogFeed(value: unknown): BlogFeed | null {
  if (!isObject(value) || !Array.isArray(value.posts)) return null;
  return {
    posts: value.posts.map(post).filter((entry): entry is BlogPost => entry !== null),
    blogUrl: isHttp(value.blogUrl) ? value.blogUrl : null,
    fallback: value.fallback === true,
  };
}
